import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import { getInstanceId } from "./common/serviceUtils";

type RejectFn = (value: string) => unknown;

//User Story: CCPS:3722 Added a Common function for getCallHistory and AddCallHistory to avoid page refresh
const getHistroyData = async (
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: "/callHistory?apiName=addCallHistory",
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `history`,
      message: `Something went wrong in getCallHistory API => ${error}`,
    });
    logMessage(
      `history`,
      `Something went wrong in getCallHistory API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getCallHistory api endpoint");
  }
};

export const getCallHistory = createAsyncThunk<unknown, void>(
  "history/getCallHistory",
  async (_args, { rejectWithValue }) => await getHistroyData(rejectWithValue),
);

export const addCallHistory = createAsyncThunk<
  unknown,
  Record<string, unknown>
>(
  "history/addCallHistory",
  async (currentSaveAttributes, { rejectWithValue }) => {
    try {
      await awsClientGateway({
        pathTemplate: "/callHistory",
        method: "POST",
        body: { ...currentSaveAttributes },
      });

      return await getHistroyData(rejectWithValue);
    } catch (error) {
      clientLog({
        component: `history`,
        message: `Something went wrong in addCallHistory API => ${error}`,
      });
      logMessage(
        `history`,
        `Something went wrong in addCallHistory API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in addCallHistory api endpoint");
    }
  },
);

export const updateCallHistory = createAsyncThunk<
  Record<string, unknown>,
  Record<string, unknown>
>(
  "history/updateCallHistory",
  async (updateHistoryData, { rejectWithValue }) => {
    try {
      await awsClientGateway({
        pathTemplate: "/callHistory",
        method: "PUT",
        body: { ...updateHistoryData },
      });
      const result = {
        ...updateHistoryData,
      };
      return result;
    } catch (error) {
      clientLog({
        component: `history`,
        message: `Something went wrong in updateCallHistory API => ${error}`,
      });
      logMessage(
        `history`,
        `Something went wrong in updateCallHistory API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in updateCallHistory api endpoint");
    }
  },
);

export const setHistoryDialNumberValidateStatus = createAction<string | null>(
  "history/setHistoryDialNumberValidateStatus",
);

export interface TranscriptArg {
  contactId: string;
  bucketName: string;
  areTranscriptsEnabledForInstance: boolean;
  timestamp: string;
}

export interface TranscriptResponse {
  transcripts?: Array<{
    BeginOffsetMillis: number;
    EndOffsetMillis: number;
    ParticipantId: string;
    Content: string;
    Sentiment?: string;
  }>;
  [key: string]: unknown;
}

export const getTranscriptDataByContactId = createAsyncThunk<
  TranscriptResponse | undefined,
  TranscriptArg,
  { state: RootState }
>(
  "history/getTranscripts",
  async (
    {
      contactId,
      bucketName,
      areTranscriptsEnabledForInstance,
      timestamp,
    }: TranscriptArg,
    { getState, rejectWithValue },
  ) => {
    try {
      const connectedInstance = getState().userInstance.connectedInstance;
      const instanceId = connectedInstance?.instanceId ?? "";
      const response = await awsClientGateway({
        pathTemplate: `/callHistory?contactId=${contactId}&instanceId=${instanceId}&areTranscriptsEnabledForInstance=${areTranscriptsEnabledForInstance}&bucketName=${bucketName}`,
        method: "GET",
      });
      if (!response?.data) {
        return rejectWithValue("Error in getTranscripts api endpoint");
      }
      return response?.data.find(
        (history: Record<string, unknown>) =>
          history?.["ContactID"] === contactId &&
          history?.timestamp === timestamp,
      );
    } catch (error) {
      clientLog({
        component: `history`,
        message: `Something went wrong in getTranscriptsAPI API => ${error}`,
      });
      logMessage(
        `history`,
        `Something went wrong in getTranscripts API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in getTranscripts api endpoint");
    }
  },
);
